import {
  allowedFunctionalCookiesForPanel,
  allAllowedCookiesForPanel,
  allAllowedCookies,
  allowedFunctionalCookies,
} from 'config/allowed-cookies'
import { AllAllowedCookies, CookiesArray } from 'models/cookies'
import { getAllowedCookies } from 'services/cookie-service'
import {
  isPanelUrl,
  isInternalUrl,
  createUrlWithParams,
  removeUrlParams,
  decodeUrlParams,
  renameQueryParam,
} from 'utils/urls'

declare global {
  interface Window {
    // object from Cookiebot script (partner's consent management platform, loaded via GTM)
    Cookiebot?: {
      consent: {
        marketing: boolean
      }
    }
  }
}

export const shouldAddCookiesAsParams = async (currentHostname: URL, targetHostname: URL): Promise<boolean> => {
  return currentHostname.origin !== targetHostname.origin && (await isInternalUrl(targetHostname))
}

const handleConditionalRedirects = (): void => {
  const currentUrl = new URL(window.location.href)

  renameQueryParam('clickId', 'uap')

  document.addEventListener('click', async (event) => {
    let $targetAnchor: HTMLAnchorElement | null = null
    if (event.target instanceof HTMLElement) $targetAnchor = event.target.closest<HTMLAnchorElement>('a')
    if (!$targetAnchor) return

    event.preventDefault() // it's important that this is only after checking if it's a anchor element

    const targetUrl = new URL($targetAnchor.href)
    let newWindow: Window | null = null

    if ($targetAnchor.target === '_blank') {
      newWindow = window.open('about:blank', '_blank')
      if (newWindow) {
        newWindow.opener = null
      } else {
        console.warn('Pop-up window was blocked. Redirecting current window instead.')
      }
    }

    let cookiesArray: CookiesArray = {}

    if (await shouldAddCookiesAsParams(currentUrl, targetUrl)) {
      let allowedCookies: AllAllowedCookies[]
      if (window.Cookiebot?.consent?.marketing) {
        allowedCookies = isPanelUrl(targetUrl) ? allAllowedCookiesForPanel : allAllowedCookies
      } else {
        allowedCookies = isPanelUrl(targetUrl) ? allowedFunctionalCookiesForPanel : allowedFunctionalCookies
      }
      cookiesArray = getAllowedCookies(allowedCookies)
    }

    const targetParams = decodeUrlParams(targetUrl.href)
    const currentParams = decodeUrlParams(window.location.href)
    const allowedKeys = allAllowedCookies as string[]
    const whitelistedCurrentParams = Object.fromEntries(
      Object.entries(currentParams).filter(([key]) => allowedKeys.includes(key)),
    )
    // Position of merging is important: whitelistedCurrentParams should be overridden
    // by targetParams, which should be overridden by cookiesArray
    const mergedParams = { ...whitelistedCurrentParams, ...targetParams, ...cookiesArray }

    // Remove duplicate parameters
    Object.keys(mergedParams).forEach((key) => {
      if (Array.isArray(mergedParams[key])) {
        mergedParams[key] = [...new Set(mergedParams[key])]
      }
    })

    const finalUrl = createUrlWithParams(mergedParams, targetUrl)

    if (newWindow) {
      newWindow.location.href = finalUrl
    } else {
      window.location.href = finalUrl
    }
  })
}

const clearNonMarketingCookiesFromUrl = (): void => {
  const params = getAllowedCookies(allowedFunctionalCookies)
  const url = removeUrlParams(window.location.href, Object.keys(params))
  window.history.replaceState(null, '', url)
}

export const initConditionalRedirects = (): void => {
  clearNonMarketingCookiesFromUrl()
  handleConditionalRedirects()
}
